import React from 'react';
import {
  WIZARD_QUESTIONS,
  currentWizardQuestion,
  normalizeWizardIndex,
} from './plantingWizardUtils';

export function PlantingWizardStep({
  answers,
  questionIndex,
  onSelectOption,
  onSelectQuestion,
  readOnly = false,
  place = null,
}) {
  const index = normalizeWizardIndex(questionIndex);
  const q = currentWizardQuestion(index, place);
  const selectedId = answers?.[q.key] || '';

  const touchRef = React.useRef({ x: null, y: null });

  const handleTouchStart = (event) => {
    const t = event.touches?.[0];
    if (!t) return;
    touchRef.current = { x: t.clientX, y: t.clientY };
  };

  const handleTouchEnd = (event) => {
    const t = event.changedTouches?.[0];
    const start = touchRef.current;
    if (!t || start.x == null) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    touchRef.current = { x: null, y: null };
    if (Math.abs(dx) < 55 || Math.abs(dy) > Math.abs(dx)) return;
    if (dx < 0) {
      if (!selectedId) return;
      if (index < WIZARD_QUESTIONS.length - 1) onSelectQuestion(index + 1);
    } else if (index > 0) {
      onSelectQuestion(index - 1);
    }
  };

  return (
    <section
      className="plant-wizard"
      data-testid="plant-wizard-step"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="plant-wizard-mobile-bar" data-testid="plant-wizard-mobile-bar">
        <span>
          STEP {String(index + 1).padStart(1, '0')} OF 5 · {q.stripLabel}
        </span>
        <span className="plant-wizard-mobile-bar__hint">SWIPE TO MOVE</span>
      </div>

      <div className="plant-wizard-dots" role="tablist" aria-label="Wizard questions">
        {WIZARD_QUESTIONS.map((item, i) => {
          const done = Boolean(answers?.[item.key]);
          const cur = i === index;
          const mark = done && !cur ? '✓' : String(i + 1).padStart(2, '0');
          return (
            <div key={item.key} className="plant-wizard-dots__item">
              <button
                type="button"
                role="tab"
                aria-selected={cur}
                className={`plant-wizard-dot${cur ? ' is-current' : ''}${done ? ' is-done' : ''}`}
                onClick={() => onSelectQuestion(i)}
                data-testid={`plant-wizard-dot-${item.key}`}
              >
                <span className="plant-wizard-dot__mark">{mark}</span>
                <span className="plant-wizard-dot__label">{item.stripLabel}</span>
              </button>
              {i < WIZARD_QUESTIONS.length - 1 ? (
                <span
                  className={`plant-wizard-dots__rule${done ? ' is-done' : ''}`}
                  aria-hidden="true"
                />
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="plant-wizard-card">
        <div className="plant-wizard-card__head">
          <span>{q.kicker}</span>
          <span className="plant-wizard-card__count">
            {index + 1} / {WIZARD_QUESTIONS.length}
          </span>
        </div>
        <div className="plant-wizard-card__body">
          <div>
            <h2 className="plant-wizard-card__title">{q.title}</h2>
            <p className="plant-wizard-card__blurb">{q.blurb}</p>
          </div>

          <div className="plant-wizard-options" role="radiogroup" aria-label={q.title}>
            {q.options.map((opt) => {
              const on = selectedId === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  className={`plant-wizard-option${on ? ' is-selected' : ''}`}
                  onClick={() => {
                    if (!readOnly) onSelectOption(opt.id);
                  }}
                  disabled={readOnly}
                  data-testid={`plant-wizard-option-${q.key}-${opt.id}`}
                >
                  <span className="plant-wizard-option__tick" aria-hidden="true">
                    {on ? '✓' : ''}
                  </span>
                  <span className="plant-wizard-option__copy">
                    <span className="plant-wizard-option__label">{opt.label}</span>
                    <span className="plant-wizard-option__note">{opt.note}</span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="plant-wizard-why">
            <span className="plant-wizard-why__mark" aria-hidden="true">i</span>
            <span className="plant-wizard-why__text">{q.why}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
